import moment from 'moment';
import { createListofDays, weekAndYearConverter } from '../utils/week';
import type {
  AlertData,
  ErrorState,
  FreezeDateData,
  PastWeekLeaveMismatches,
  ProjectIdList,
  ProjectListItem,
  RightPanelDivClassNameProperty,
  SelectedDate,
  SelectedTaskValue,
  StandardCode,
  SubmitValidationError,
  TimeSheet,
  UnassignedProject,
} from '../types';

/**
 * The state shape of `mainMenu.jsx` (jsx:52-160), extracted from the constructor.
 *
 * Reproduced whole, including the keys nothing ever reads. Under `noUnusedLocals` an
 * unused *local* is a compile error but an unused object *property* is not, so keeping
 * them costs nothing — and keeping the `setState` calls that write them preserves the
 * exact number and ordering of re-renders the class produced.
 *
 * ## The duplicate `error` key
 *
 * The original object literal declares `error` twice — at jsx:55 and again at jsx:66 —
 * with byte-identical values, so the second silently wins and the effective shape is a
 * single `{ status: false, message: "" }`. That effective shape is what appears below.
 * It is documented rather than "fixed": the duplication is a defect in the source, but it
 * has no behavioral consequence, and inventing a second error slice to justify the second
 * declaration would.
 */

/** The submit-validation modal's three error buckets. */
export interface ErrorModelState {
  openErrorModel: boolean;
  minimumHourExceedError: SubmitValidationError;
  maximumHourExceedError: SubmitValidationError;
  invoiceCommentMissingError: SubmitValidationError;
}

export interface TimeEntryState {
  /** Seeded from the runtime config's banner/feedback flags — see `createInitialTimeEntryState`. */
  bannerVisible: boolean;
  feedbackVisible: boolean;

  error: ErrorState;
  errorModel: ErrorModelState;
  /** Declared in the original's initial state and never read or written since. */
  errorMessage: unknown[];

  loading: boolean;
  initialLoading: boolean;
  /** Written by the week-change branch of componentDidUpdate; never read. */
  refresh: boolean;

  selectedDate: SelectedDate;

  /** Declared and never used. Not to be confused with the local of the same name. */
  sortedUserTimeSheetData: TimeSheet[];
  /** Declared and never used. */
  temp: unknown[];

  userTimeSheetData: TimeSheet[];
  /** Only ever written by `getGlobalTasks`, which is never called — so always `[]`. */
  nonProjectTask: StandardCode[];
  /** Only ever written by the unused `getUnassignedTasks` path — so always `[]`. */
  unassignedTasks: UnassignedProject[];
  projectList: ProjectListItem[];
  /**
   * `undefined`, not `false`, when no project is copy-enabled: the original computes it as
   * `projectList?.find(…) && true`, which yields `true` or `undefined` (jsx:478-482). The
   * distinction is visible one level down — `AddTask` compares this prop by reference, so
   * the initial `false` → `undefined` transition triggers a re-render that a `false` →
   * `false` transition would not. Narrowing it to `boolean` would quietly drop that.
   */
  isCopyUnassigedTaskEnabled: boolean | undefined;
  selectedTaskValue: SelectedTaskValue;

  loggedUserGteEmployeeNo: number;
  operationalUserGteEmployeeNo: number;

  datePicker: string;
  rightPanelDivClassNameProperty: RightPanelDivClassNameProperty;
  projectListIds: ProjectIdList;
  alertData: AlertData | null;
  saveDraft: boolean;
  openCalender: boolean;
  disableNextBtn: boolean;
  isHandleSaveDraftCalled: boolean;
  deleteTaskData: unknown;
  deleteGteProjectId: number | null;
  freezeDateData: FreezeDateData;

  /**
   * Guards every time input in the grid (the original's comment, jsx:138-146):
   * false means the last edited total failed the "< 24 hours" check, and until it is
   * fixed no other input may be focused and the week/save/submit buttons are disabled.
   */
  clickInput: boolean;

  unfreezeModal: boolean;
  response: boolean;
  callUnassignTask: boolean;
  responseMessage: string;
  /** Always true; there is no code path that sets it false. */
  showWarningIcon: boolean;
  isTaskDone: boolean;
  pastWeekLeaveMismatchesDetails: PastWeekLeaveMismatches;

  /*
   * Written by `setState` but absent from the initial literal, so `undefined` until then.
   * Declared here because the class had no shape to violate and these writes must survive.
   */
  /** `setSelectedDelegateUser` writes it; nothing reads it (the props are read instead). */
  gteEmployeeNo?: number;
  /** `selectedDatePicker` writes it; nothing reads it. */
  selectedTaskTab_class?: string;
  /** Feedback's `hide` callback writes it; nothing reads it. */
  toast?: boolean;
  /** Read once, as the right panel's `addTask` prop; never written, so always undefined. */
  addTask?: unknown;
}

/**
 * The initial state, built exactly as the constructor built it.
 *
 * `banner.showBanner` and `showFeedback` came from the bundled `property.json` in the old
 * app and now arrive from `GET /api/auth-config`, so they are parameters rather than a
 * module-level import. Everything else is computed from "now" at construction time, which
 * is why this is a factory and not a constant: `useLegacyState` calls it once per mount,
 * matching a class constructor.
 */
export function createInitialTimeEntryState(options: {
  showBanner: boolean;
  showFeedback: boolean;
}): TimeEntryState {
  const firstDayofCurrentWeek = moment().startOf('isoWeek').format('YYYY-MM-DD');

  return {
    bannerVisible: options.showBanner,
    feedbackVisible: options.showFeedback,
    error: {
      status: false,
      message: '',
    },
    errorModel: {
      openErrorModel: false,
      minimumHourExceedError: { error: false, errorList: [] },
      maximumHourExceedError: { error: false, errorList: [] },
      invoiceCommentMissingError: { error: false, errorList: [] },
    },
    errorMessage: [],
    loading: false,
    initialLoading: true,
    refresh: true,

    selectedDate: {
      // get and set current date, year and week
      firstDayofCurrentWeek: firstDayofCurrentWeek,
      firstDayofWeekForUnfreeze: moment().startOf('isoWeek').format('DD-MMM-YYYY'),
      year: parseInt(weekAndYearConverter(firstDayofCurrentWeek)?.Y),
      week: parseInt(weekAndYearConverter(firstDayofCurrentWeek)?.W),
      dateOfFirstDayofCurrentWeek: moment().startOf('isoWeek').format('DD MMMM'),
      dateOfLastDayofCurrentWeek: moment().endOf('isoWeek').format('DD MMMM'),
      days: createListofDays(firstDayofCurrentWeek),
      today: moment().format('YYYY-MM-DD'),
      currentWeek: parseInt(moment().format('WW')),
      currentDate: new Date(),
      maxNextNavigateDate: new Date(new Date().getTime() + 28 * 24 * 60 * 60 * 1000),
      /*
       * `parseInt(moment(new Date("2015")).format("YYYY-MM-DD"))` — parseInt stops at the
       * first "-", so this is the NUMBER 2015 despite the date formatting around it. The
       * only reader compares it against `parseInt(selectedDate.year)` (jsx:2434), so the
       * value is right even though the expression is a roundabout way of writing it.
       * Kept verbatim.
       */
      maxPastNavigateYear: parseInt(moment(new Date('2015')).format('YYYY-MM-DD')),
    },

    sortedUserTimeSheetData: [],
    temp: [],
    userTimeSheetData: [],
    nonProjectTask: [],
    unassignedTasks: [],
    projectList: [],
    isCopyUnassigedTaskEnabled: false,
    selectedTaskValue: {},
    loggedUserGteEmployeeNo: 0,
    operationalUserGteEmployeeNo: 0,
    datePicker: '',

    /*
     * The accordion's open/closed state, held as literal Bootstrap class strings. Note the
     * trailing space in the alerts button class: it is in the original, App.css is carried
     * byte-identical, and `handlePanelCollapse` compares this value against
     * "accordion-button collapsed" WITHOUT the space — so the alerts section does not
     * toggle on its first click after a project load. Preserved deliberately.
     */
    rightPanelDivClassNameProperty: {
      alerts: {
        button: 'accordion-button collapsed ',
        div: 'accordion-collapse collapse',
      },
      addTask: {
        button: 'accordion-button collapsed',
        div: 'accordion-collapse collapse',
      },
      selectedTask: {
        button: 'accordion-button',
        div: 'accordion-collapse collapse show',
      },
    },

    projectListIds: [],
    alertData: null,
    saveDraft: false,
    openCalender: false,
    disableNextBtn: false,
    isHandleSaveDraftCalled: false,
    deleteTaskData: {},
    deleteGteProjectId: null,
    freezeDateData: {
      configType: 'FREEZE_DATE',
      configValue: '',
      isFreezed: false,
      isUpdated: false,
    },
    clickInput: true,
    unfreezeModal: false,
    response: false,
    callUnassignTask: false,
    responseMessage: '',
    showWarningIcon: true,
    isTaskDone: false,
    pastWeekLeaveMismatchesDetails: {
      status: '',
      message: '',
      dates: [],
      isUpdated: false,
    },
  };
}
